import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import PageContainer from 'components/@layout/PageContainer';
import BreakableTypography from 'components/@common/BreakableTypography';
import EmptyMessage from 'components/@common/EmptyResultSetMessage';
import ListViewContainer from 'components/@common/ListViewContainer';
import MoreVertMenu from 'components/@common/MoreVertMenu';
import ProductIconList from 'components/@common/ProductIconList';
import TileViewContainer, { BaseCardData } from 'components/@common/TileViewContainer';
import GET_USER_CLASSROOMS from 'graph/classroom/get-user-classrooms-query';
import useClassroomActionOptionsMenuProps from 'hooks/use-classroom-action-options-menu';
import Typography from '@material-ui/core/Typography';
import useStore from 'store';
import { ViewStyle } from 'store/types';
import { ModuleProps } from 'types';
import { Classroom } from 'types/schema-generated-types';
import { proxyInsufficientPrivilegesError } from 'utilities/proxy-apollo-error';
import { getEducatorNames } from 'utilities/get-classroom-educators';
import ClassroomCardContentFragment from './components/ClassroomCardContentFragment';
import HeaderBar from '../HeaderBar';
import { ClassroomFilterValue } from '../types';

const Classrooms: React.FC<ModuleProps> = props => {
  const history = useHistory();
  const [{ ui, user }] = useStore();
  const { t } = useTranslation(props.id);
  const { data, loading: classroomsLoading, error } = useQuery(GET_USER_CLASSROOMS, {
    variables: { userId: user.me?.id },
    errorPolicy: 'all',
    skip: !user.me?.id
  });
  const {
    loading: classroomMutationLoading,
    actionOptions,
    archivedOptions
  } = useClassroomActionOptionsMenuProps({ translationId: props.id, classrooms: data?.classrooms });

  const [loading, setLoading] = React.useState(false);
  const [classroomFilter, setClassroomFilter] = React.useState<ClassroomFilterValue>(
    ClassroomFilterValue.ACTIVE
  );

  const currentOptions =
    classroomFilter === ClassroomFilterValue.ACTIVE ? actionOptions : archivedOptions;

  const editModalopen = ui.modal.isOpen && ui.modal.routeAction === 'edit-class';

  const handleFilterChange = (value: ClassroomFilterValue): void => {
    setClassroomFilter(value);
  };

  const handleClassroomClick = React.useCallback(
    (item: Classroom) => {
      history.push(`${history.location.pathname}/${item.id}`);
    },
    [history]
  );

  const classrooms = React.useMemo(() => {
    if (!data?.classrooms) return [];

    return data.classrooms
      .filter((c: Classroom) =>
        classroomFilter === ClassroomFilterValue.ACTIVE ? !c.dateArchived : c.dateArchived
      )
      .map((c: Classroom) => ({
        name: c.name,
        id: c.id,
        classroomId: c.id,
        organizationId: c.organizationId,
        organizationName: c.organization?.name,
        color: c.color,
        licenseUsage: c.licenseUsage,
        studentCount: c.stats?.studentCount ?? 0,
        ...getEducatorNames(c)
      }));
  }, [data, classroomFilter]);

  const isListView = classrooms.length && ui.viewStyle === ViewStyle.LIST && ui.isDesktop;
  const isTileView = classrooms.length && ui.viewStyle === ViewStyle.TILE;

  const cardData = React.useMemo((): BaseCardData[] => {
    if (!isTileView) return [];

    return classrooms.map((classroom: any) => ({
      ...classroom,
      title: classroom.name,
      onClick: () => handleClassroomClick(classroom),
      ContentComponent: (
        <ClassroomCardContentFragment
          educator={classroom.educator}
          coeducators={classroom.coeducators}
          organizationName={classroom.organizationName}
          studentCount={classroom.studentCount}
          licenseUsage={classroom.licenseUsage}
        />
      )
    }));
  }, [classrooms, handleClassroomClick, isTileView]);

  const columns = React.useMemo(
    () => [
      {
        label: 'Class Name',
        sortable: true,
        sortKey: 'title',
        getValue(item: any): any {
          return <BreakableTypography variant="h6">{item.name}</BreakableTypography>;
        }
      },
      {
        label: 'Educator',
        sortable: true,
        sortKey: 'educator',
        getValue(item: any): any {
          return <Typography>{item.educator}</Typography>;
        }
      },
      {
        label: 'Co-Educators',
        sortable: true,
        sortKey: 'coeducators',
        getValue(item: any): any {
          return <Typography>{item.coeducators}</Typography>;
        }
      },
      {
        label: 'Organization',
        sortable: true,
        sortKey: 'organizationName',
        getValue(item: any): any {
          return <Typography>{item.organizationName}</Typography>;
        }
      },
      {
        label: 'No. of Students',
        sortable: true,
        sortKey: 'studentCount',
        getValue(item: any): any {
          return <Typography>{item.studentCount}</Typography>;
        }
      },
      {
        label: 'Products',
        sortable: false,
        sortKey: 'products',
        getValue(item: any): any {
          return <ProductIconList style={{ minWidth: 120 }} licenseUsage={item.licenseUsage} />;
        }
      },
      {
        label: '',
        sortable: false,
        getValue(item: any): any {
          return (
            <MoreVertMenu
              actionOptions={currentOptions}
              id={item.id}
              context={{
                classroomId: item.id,
                organizationId: item.organizationId
              }}
            />
          );
        }
      }
    ],
    [currentOptions]
  );

  return (
    <PageContainer
      loading={
        (loading || classroomsLoading || classroomMutationLoading || !user.me?.id) && !editModalopen
      }
      error={proxyInsufficientPrivilegesError(error)}
    >
      <HeaderBar
        setLoading={setLoading}
        onFilterChange={handleFilterChange}
        selectedFilter={classroomFilter}
        {...props}
      />
      {isListView && (
        <ListViewContainer
          rowData={classrooms}
          columns={columns}
          onRowClick={handleClassroomClick}
        />
      )}
      {isTileView && (
        <TileViewContainer cardData={cardData} actionOptions={currentOptions as any} />
      )}
      {!classrooms.length && (
        <EmptyMessage
          style={{ marginTop: '12px' }}
          message={t(`No {{type}} Classes`, {
            type: classroomFilter === ClassroomFilterValue.ACTIVE ? 'Active' : 'Archived'
          })}
          subtitle={t(`You don't have any {{type}} classes.`, {
            type: classroomFilter === ClassroomFilterValue.ACTIVE ? 'active' : 'archived'
          })}
        />
      )}
    </PageContainer>
  );
};

export default Classrooms;
